import { call, delay, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import type { Campaign, CampaignToast } from '../../components/campaigns/data/types';
import { ACTION_LABELS } from '../../components/campaigns/utils/campaignStatus';
import { mergeCustomers as customersMerged, recordOptOut } from '../customers/customerActions';
import { buildOptOutRecord } from '../customers/customerConsent';
import { CUSTOMERS_MERGE, type CustomersState } from '../customers/customerTypes';
import * as api from './campaignsApi';
import * as A from './campaignsActions';
import * as T from './campaignsTypes';
import type { MutationMeta } from './campaignsTypes';

const TOAST_MS = 3500;
/** Recorded on opt-outs marked from the review queue; there is no sign-in identity in the mock. */
const INBOUND_REVIEWER = 'You';

const messageOf = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Try again.');

function* removeToastLater(id: string) {
  yield delay(TOAST_MS);
  yield put(A.toastRemove(id));
}

function* toast(message: string, variant: CampaignToast['variant'] = 'success') {
  const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  yield put(A.toastAdd({ id, message, variant }));
  yield fork(removeToastLater, id);
}

function* failMutation(e: unknown, meta: MutationMeta<any>) {
  const message = messageOf(e);
  yield put(A.mutationFailure(message));
  yield* toast(message, 'error');
  meta.onFailure?.(message);
}

// ── Customers ──

/**
 * MOCK ONLY. Hands the mock service the customers and segments held by the Customers module, at start-up
 * and after every change. The live service reads the same customer database, so this goes away with the mock.
 */
function* syncMockDirectory() {
  const { customers, segments }: CustomersState = yield select((s: { customers: CustomersState }) => s.customers);
  api.setMockDirectory({ customers, segments });
}

const isCustomerDataChange = (action: { type: string }) =>
  action.type.startsWith('customers/') && !action.type.startsWith('customers/TOAST_');

/** LNK-06 — rewards and campaign history follow a merged profile to the one that was kept. */
function* followMerge({ payload }: ReturnType<typeof customersMerged>) {
  try {
    const data: api.CustomerActivityPayload = yield call(api.reassignCustomers, payload.removeIds, payload.keepId);
    yield put(A.fetchActivitySuccess(data));
  } catch (e) {
    yield* toast(messageOf(e), 'error');
  }
}

function* fetchActivity() {
  try {
    const data: api.CustomerActivityPayload = yield call(api.listCustomerActivity);
    yield put(A.fetchActivitySuccess(data));
  } catch (e) {
    yield put(A.fetchActivityFailure(messageOf(e)));
  }
}

// ── Audience ──

function* previewAudience({ payload }: ReturnType<typeof A.previewAudienceRequest>) {
  try {
    const counts: Awaited<ReturnType<typeof api.previewAudience>> = yield call(api.previewAudience, payload);
    yield put(A.previewAudienceSuccess(counts));
  } catch {
    yield put(A.previewAudienceFailure());
  }
}

// ── Campaigns ──

function* fetchCampaigns() {
  try {
    const campaigns: Campaign[] = yield call(api.listCampaigns);
    yield put(A.fetchCampaignsSuccess(campaigns));
  } catch (e) {
    yield put(A.fetchCampaignsFailure(messageOf(e)));
  }
}

function* saveCampaign({ payload, meta }: ReturnType<typeof A.saveCampaignRequest>) {
  try {
    const campaign: Campaign = yield call(api.saveCampaign, payload);
    yield put(A.saveCampaignSuccess(campaign));
    if (!meta.silent) yield* toast('Draft saved');
    meta.onSuccess?.(campaign);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

const TRANSITION_TOASTS: Record<string, string> = {
  schedule: 'Campaign scheduled',
  send_now: 'Campaign is sending',
  enable: 'Automation turned on',
  unschedule: 'Campaign moved back to draft',
  cancel: 'Campaign cancelled. Messages already sent are not recalled',
  pause: 'Paused. Unsent messages are on hold',
  resume: 'Resumed',
  archive: 'Archived',
};

function* transitionCampaign({ payload, meta }: ReturnType<typeof A.transitionCampaignRequest>) {
  try {
    const campaign: Campaign = yield call(api.transitionCampaign, payload.id, payload.action, payload.scheduledAt);
    yield put(A.transitionCampaignSuccess(campaign));
    yield* toast(TRANSITION_TOASTS[payload.action] ?? ACTION_LABELS[payload.action]);
    meta.onSuccess?.(campaign);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

function* duplicateCampaign({ payload, meta }: ReturnType<typeof A.duplicateCampaignRequest>) {
  try {
    const campaign: Campaign = yield call(api.duplicateCampaign, payload);
    yield put(A.duplicateCampaignSuccess(campaign));
    yield* toast(`Created "${campaign.name}" as a draft`);
    meta.onSuccess?.(campaign);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

function* deleteCampaign({ payload, meta }: ReturnType<typeof A.deleteCampaignRequest>) {
  try {
    yield call(api.deleteCampaign, payload);
    yield put(A.deleteCampaignSuccess(payload));
    yield* toast('Campaign deleted');
    meta.onSuccess?.(payload);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

function* testSend({ payload, meta }: ReturnType<typeof A.testSendRequest>) {
  try {
    yield call(api.testSend, payload.campaignId, payload.channel, payload.destination);
    yield* toast(`Test ${payload.channel === 'sms' ? 'SMS' : payload.channel} sent to ${payload.destination}`);
    meta.onSuccess?.(payload);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

function* revokeGrants({ payload, meta }: ReturnType<typeof A.revokeGrantsRequest>) {
  try {
    const result: Awaited<ReturnType<typeof api.revokeGrants>> = yield call(api.revokeGrants, payload);
    yield put(A.revokeGrantsSuccess(payload));
    yield* toast(`${result.revoked} unredeemed reward${result.revoked === 1 ? '' : 's'} revoked`);
    meta.onSuccess?.(result);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

// ── Report ──

function* fetchReport({ payload }: ReturnType<typeof A.fetchReportRequest>) {
  try {
    const data: api.ReportPayload = yield call(api.getCampaignReport, payload);
    yield put(A.fetchReportSuccess(payload, data));
  } catch (e) {
    yield put(A.fetchReportFailure(messageOf(e)));
  }
}

// ── Settings ──

function* fetchSettings() {
  try {
    const data: api.SettingsPayload = yield call(api.getSettings);
    yield put(A.fetchSettingsSuccess(data));
  } catch (e) {
    yield put(A.fetchSettingsFailure(messageOf(e)));
  }
}

function* saveSettings({ payload, meta }: ReturnType<typeof A.saveSettingsRequest>) {
  try {
    const settings: Awaited<ReturnType<typeof api.saveSettings>> = yield call(api.saveSettings, payload);
    yield put(A.saveSettingsSuccess(settings));
    yield* toast('Settings saved');
    meta.onSuccess?.(settings);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

function* buyCredits({ payload, meta }: ReturnType<typeof A.buyCreditsRequest>) {
  try {
    const data: api.SettingsPayload = yield call(api.buyCredits, payload);
    yield put(A.fetchSettingsSuccess(data));
    yield* toast('Credits added to your balance');
    meta.onSuccess?.(data);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

// ── Opt-out review queue ──

function* fetchInbound() {
  try {
    const inbound: Awaited<ReturnType<typeof api.listInbound>> = yield call(api.listInbound);
    yield put(A.fetchInboundSuccess(inbound));
  } catch (e) {
    yield put(A.fetchInboundFailure(messageOf(e)));
  }
}

function* resolveInbound({ payload, meta }: ReturnType<typeof A.resolveInboundRequest>) {
  try {
    const result: api.ResolveInboundResult = yield call(api.resolveInbound, payload.id, payload.resolution);
    yield put(A.resolveInboundSuccess(result));
    // CON-09 — the opt-out is written to the customer's consent history, where every send checks it.
    if (result.optOutCustomerId) {
      const at = new Date().toISOString();
      yield put(recordOptOut({ id: result.optOutCustomerId, record: buildOptOutRecord(`con-${result.inbound.id}`, 'sms', 'portal_staff', INBOUND_REVIEWER, at) }));
    }
    yield* toast(payload.resolution === 'opt_out' ? 'Marked as opt-out. The customer will get no more marketing SMS' : 'Message dismissed');
    meta.onSuccess?.(result);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

// ── Tracking sources ──

function* fetchSources() {
  try {
    const sources: Awaited<ReturnType<typeof api.listSources>> = yield call(api.listSources);
    yield put(A.fetchSourcesSuccess(sources));
  } catch (e) {
    yield put(A.fetchSourcesFailure(messageOf(e)));
  }
}

function* saveSource({ payload, meta }: ReturnType<typeof A.saveSourceRequest>) {
  try {
    const source: Awaited<ReturnType<typeof api.saveSource>> = yield call(api.saveSource, payload);
    yield put(A.saveSourceSuccess(source));
    if (!meta.silent) yield* toast(`Source "${source.name}" ${payload.id ? 'updated' : 'created'}`);
    meta.onSuccess?.(source);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

function* deleteSource({ payload, meta }: ReturnType<typeof A.deleteSourceRequest>) {
  try {
    yield call(api.deleteSource, payload);
    yield put(A.deleteSourceSuccess(payload));
    yield* toast('Source deleted');
    meta.onSuccess?.(payload);
  } catch (e) {
    yield* failMutation(e, meta);
  }
}

// ── Overview ──

function* fetchOverview({ payload }: ReturnType<typeof A.fetchOverviewRequest>) {
  try {
    const stats: Awaited<ReturnType<typeof api.getOverview>> = yield call(api.getOverview, payload);
    yield put(A.fetchOverviewSuccess(stats));
  } catch (e) {
    yield put(A.fetchOverviewFailure(messageOf(e)));
  }
}

export function* campaignsSaga() {
  yield call(syncMockDirectory);
  yield takeEvery(isCustomerDataChange, syncMockDirectory);
  yield takeEvery(CUSTOMERS_MERGE, followMerge);
  yield takeLatest(T.FETCH_ACTIVITY_REQUEST, fetchActivity);
  yield takeLatest(T.PREVIEW_AUDIENCE_REQUEST, previewAudience);
  yield takeLatest(T.FETCH_CAMPAIGNS_REQUEST, fetchCampaigns);
  yield takeEvery(T.SAVE_CAMPAIGN_REQUEST, saveCampaign);
  yield takeEvery(T.TRANSITION_CAMPAIGN_REQUEST, transitionCampaign);
  yield takeEvery(T.DUPLICATE_CAMPAIGN_REQUEST, duplicateCampaign);
  yield takeEvery(T.DELETE_CAMPAIGN_REQUEST, deleteCampaign);
  yield takeEvery(T.TEST_SEND_REQUEST, testSend);
  yield takeEvery(T.REVOKE_GRANTS_REQUEST, revokeGrants);
  yield takeLatest(T.FETCH_REPORT_REQUEST, fetchReport);
  yield takeLatest(T.FETCH_SETTINGS_REQUEST, fetchSettings);
  yield takeEvery(T.SAVE_SETTINGS_REQUEST, saveSettings);
  yield takeEvery(T.BUY_CREDITS_REQUEST, buyCredits);
  yield takeLatest(T.FETCH_INBOUND_REQUEST, fetchInbound);
  yield takeEvery(T.RESOLVE_INBOUND_REQUEST, resolveInbound);
  yield takeLatest(T.FETCH_SOURCES_REQUEST, fetchSources);
  yield takeEvery(T.SAVE_SOURCE_REQUEST, saveSource);
  yield takeEvery(T.DELETE_SOURCE_REQUEST, deleteSource);
  yield takeLatest(T.FETCH_OVERVIEW_REQUEST, fetchOverview);
}
